import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { takeContinuation } from "../lib/authContinuation";
import { useAuthenticatedLanding } from "../hooks/useAuthenticatedLanding";
import "./Login.css";

function OfferTraceHero() {
  return (
    <div className="qw-auth-side qw-login-hero" aria-label="OfferTrace — бизнес платформа за оферти">
      <svg
        className="qw-login-hero-decor"
        viewBox="0 0 1200 760"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="qwHeroLine" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#a5f3fc" stopOpacity="0.95" />
            <stop offset="38%" stopColor="#67e8f9" stopOpacity="0.85" />
            <stop offset="70%" stopColor="#2dd4bf" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#0d9488" stopOpacity="0.14" />
          </linearGradient>
          <linearGradient id="qwHeroLineWide" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#5eead4" stopOpacity="0.55" />
            <stop offset="45%" stopColor="#2dd4bf" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#0f766e" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="qwHeroCircuit" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#14b8a6" stopOpacity="0" />
            <stop offset="50%" stopColor="#2dd4bf" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#5eead4" stopOpacity="0.22" />
          </linearGradient>
          {/* radial haze that gives the arc a sense of luminous volume sitting
              in space, rather than a flat line drawn on top of the background */}
          <radialGradient id="qwArcHaze" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.22" />
            <stop offset="55%" stopColor="#0f766e" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#020b12" stopOpacity="0" />
          </radialGradient>
          <filter id="qwHeroGlowCore" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="3.2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="qwHeroGlowMid" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="11" />
          </filter>
          <filter id="qwHeroGlowBloom" x="-140%" y="-140%" width="380%" height="380%">
            <feGaussianBlur stdDeviation="34" />
          </filter>
        </defs>

        {/* atmospheric haze behind the arc's trajectory — depth, not a flat line */}
        <ellipse cx="560" cy="560" rx="520" ry="300" fill="url(#qwArcHaze)" opacity="0.55" />

        {/* soft bloom pass — the widest, faintest layer */}
        <path
          d="M -90 285 C 120 720, 530 790, 845 654 C 1015 581, 1124 468, 1275 337"
          fill="none"
          stroke="#2dd4bf"
          strokeWidth="20"
          opacity="0.22"
          filter="url(#qwHeroGlowBloom)"
        />
        {/* translucent mid stroke — gives the line body/volume */}
        <path
          d="M -90 285 C 120 720, 530 790, 845 654 C 1015 581, 1124 468, 1275 337"
          fill="none"
          stroke="url(#qwHeroLineWide)"
          strokeWidth="7"
          opacity="0.7"
          filter="url(#qwHeroGlowMid)"
        />
        {/* sharp bright core */}
        <path
          d="M -90 285 C 120 720, 530 790, 845 654 C 1015 581, 1124 468, 1275 337"
          fill="none"
          stroke="url(#qwHeroLine)"
          strokeWidth="1.7"
          opacity="0.95"
          filter="url(#qwHeroGlowCore)"
        />
        {/* reflected teal light cast on the nearby background beneath the arc */}
        <ellipse cx="480" cy="640" rx="360" ry="90" fill="#0d9488" opacity="0.07" filter="url(#qwHeroGlowBloom)" />

        <path
          d="M 870 790 L 1018 647 L 1170 647 L 1270 556"
          fill="none"
          stroke="url(#qwHeroCircuit)"
          strokeWidth="1.2"
          opacity="0.82"
        />
        <path
          d="M 897 813 L 1045 680 L 1194 680 L 1275 608"
          fill="none"
          stroke="url(#qwHeroCircuit)"
          strokeWidth="1.1"
          opacity="0.58"
        />
        <circle cx="1018" cy="647" r="3.2" fill="#5eead4" opacity="0.9" filter="url(#qwHeroGlowCore)" />
        <circle cx="1194" cy="680" r="2.6" fill="#2dd4bf" opacity="0.8" filter="url(#qwHeroGlowCore)" />
        {/* brightest point along the arc's curvature — uneven intensity reads as depth */}
        <circle cx="845" cy="654" r="6" fill="#a5f3fc" opacity="0.85" filter="url(#qwHeroGlowMid)" />
        <path
          d="M 930 0 C 1125 80, 1246 245, 1268 445"
          fill="none"
          stroke="#5eead4"
          strokeWidth="1"
          opacity="0.26"
        />
      </svg>

      <div className="qw-login-brand-lockup">
        <div className="qw-login-brand-row">
          <svg className="qw-login-logo-mark" viewBox="0 0 120 120" aria-hidden="true">
            <defs>
              <linearGradient id="qwLogoStroke" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#22d3ee" />
                <stop offset="50%" stopColor="#2dd4bf" />
                <stop offset="100%" stopColor="#14b8a6" />
              </linearGradient>
              <linearGradient id="qwLogoBars" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#5eead4" />
              </linearGradient>
            </defs>
            <path
              d="M60 8 105 34v52L60 112 15 86V34Z"
              fill="rgba(2, 19, 27, 0.42)"
              stroke="url(#qwLogoStroke)"
              strokeWidth="6"
              strokeLinejoin="round"
            />
            <rect x="31" y="62" width="12" height="27" rx="1.5" fill="url(#qwLogoBars)" />
            <rect x="54" y="48" width="12" height="41" rx="1.5" fill="url(#qwLogoBars)" />
            <rect x="77" y="34" width="12" height="55" rx="1.5" fill="url(#qwLogoBars)" />
          </svg>
          <div className="qw-login-wordmark">OfferTrace</div>
        </div>
        <div className="qw-login-brand-subtitle">бизнес платформа за оферти</div>
      </div>
    </div>
  );
}

export default function Login() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const passwordChanged = !!location.state?.passwordChanged;
  // Phase 2D.2B — a fixed application-controlled state, never a query parameter.
  const signupConfirmed = !!location.state?.signupConfirmed;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Phase 2D.1B fix — once signed in with no invitation to resume, the
  // destination depends on organization membership AND platform-admin status,
  // both of which load asynchronously after the session appears. This flag
  // keeps the form busy until useAuthenticatedLanding has a real answer.
  const [awaitingLanding, setAwaitingLanding] = useState(false);
  const { loading: landingLoading, path: landingPath } = useAuthenticatedLanding();

  useEffect(() => {
    if (!awaitingLanding || landingLoading) return;
    // landingPath always comes from the fixed LANDING set in lib/landing.js —
    // never from user input, a query string or storage — so this can never
    // become an open redirect.
    nav(landingPath, { replace: true });
  }, [awaitingLanding, landingLoading, landingPath, nav]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError("");
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setBusy(false); setError(error.message); return; }
    // Phase 2D.2 checkpoint 2B — resume an invitation the person was in the middle
    // of accepting. The continuation is a PATH from a fixed allow-list, so it can
    // never become an open redirect, and the invitation token itself was never
    // part of it: it stayed in memory for the whole round trip. This still wins
    // over the default landing.
    const next = takeContinuation();
    if (next) { nav(next); return; }
    setAwaitingLanding(true);
  }

  return (
    <div className="qw-auth-wrap qw-login-wrap">
      <OfferTraceHero />
      <div className="qw-auth-panel">
        <form className="qw-auth-form" onSubmit={submit}>
          <h1>{t("auth.welcome_back")}</h1>
          <p className="lead">{t("auth.sign_in")}</p>
          {passwordChanged && (
            <div
              className="qw-card"
              style={{ background: "#ecfdf5", borderColor: "#a7f3d0", color: "#065f46", marginBottom: 14 }}
              role="status"
              data-testid="login-password-changed-notice"
            >
              {t("password.success_body")}
            </div>
          )}
          {signupConfirmed && (
            <div
              className="qw-card"
              style={{ background: "#ecfdf5", borderColor: "#a7f3d0", color: "#065f46", marginBottom: 14 }}
              role="status"
              data-testid="login-signup-confirmed-notice"
            >
              {t("signup.confirmed_notice")}
            </div>
          )}
          <div className="qw-field">
            <label>{t("auth.email")}</label>
            <input className="qw-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" autoComplete="email" />
          </div>
          <div className="qw-field">
            <label>{t("auth.password")}</label>
            <input className="qw-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" autoComplete="current-password" />
          </div>
          <div style={{ marginTop: -8, marginBottom: 14, textAlign: "right" }}>
            <Link
              to="/auth/forgot-password"
              data-testid="login-forgot-password-link"
              style={{ color: "#0f766e", fontWeight: 600, fontSize: 13 }}
            >
              {t("auth.forgot_password")}
            </Link>
          </div>
          {error && <div className="qw-error" data-testid="login-error">{error}</div>}
          <button className="qw-btn primary stretch" style={{ width: "100%", justifyContent: "center" }} type="submit" disabled={busy} data-testid="login-submit">
            {busy ? t("common.loading") : t("auth.sign_in")}
          </button>
          <div style={{ marginTop: 16, color: "#64748b", fontSize: 13 }}>
            {t("auth.no_account")} <Link to="/register" data-testid="go-register" style={{ color: "#0f766e", fontWeight: 600 }}>{t("auth.sign_up")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
